import { FunctionComponent, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  FormHelperText,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { useConfiguration } from "../../dataAccess/api/configuration";
import { useUser } from "../../dataAccess/api/user";
import { getJustificationByAlignment } from "../../builder/themeOptionsBuilder";
import TextField from "./TextField";
import { GuestFlowRedemption } from "../../dataAccess/api/deal";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import { checkIfEmailChanged, DealSettings } from "../public/Deal";
import { isGuestUser } from "../../dataAccess/api/guestUser";
import { ButtonProgress } from "./ButtonProgress";
import TermsText from "./TermsText";
import { useTranslation } from "../../helpers/i18n";
import {
  COMPANY_NAME_PLACEHOLDER_TEXT,
  EMAIL_ADDRESS_PLACEHOLDER_TEXT,
  FULL_NAME_PLACEHOLDER_TEXT,
  PHONE_NUMBER_PLACEHOLDER_TEXT,
} from "../../../theme/defaultConstants";
import HTMLRichText from "./HTMLRichText";

interface Props
  extends Pick<
    DealSettings,
    | "redemptionButtons"
    | "companyNamePlaceholderText"
    | "emailAddressPlaceholderText"
    | "fullNamePlaceholderText"
    | "phoneNumberPlaceholderText"
    | "form"
  > {
  onSubmit: (data: GuestFlowRedemption) => void;
  loading?: boolean;
  guestUserCreated: boolean;
  coloredVariant: boolean;
}

const StyledForm = styled("form")<Pick<Props["form"], "alignment">>(
  ({ theme, alignment }) => ({
    display: "flex",
    gap: theme.spacing(2),
    flexDirection: "column",
    alignItems: getJustificationByAlignment(alignment),
  }),
);

const CheckboxFieldContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const StyledInputsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  alignItems: "center",
  gap: "1rem",
  [theme.breakpoints.up("lg")]: {
    flexWrap: "nowrap",
    justifyContent: "initial",
  },
}));

const Terms = styled(FormControlLabel)(() => ({
  margin: 0,
}));

const StyledCheckbox = styled(
  (props: CheckboxProps & { customColor?: string }) => (
    <Checkbox
      disableRipple
      sx={{
        color: props.customColor,
        "&.Mui-checked": {
          color: props.customColor,
        },
      }}
      {...props}
    />
  ),
)(() => ({
  paddingLeft: 0,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    "& .MuiFilledInput-root": {
      marginBottom: "0",
    },
    width: "auto",
  },
}));

export const StyledButton = styled(ThemeButton)<{ errors: boolean }>(
  ({ errors, theme }) => ({
    marginRight: "0",
    marginBottom: "0.5rem",
    minWidth: "8rem",
    width: "100%",
    marginTop: errors ? "0.4rem" : 0,
    alignSelf: errors ? "baseline" : "center",
    [theme.breakpoints.up("md")]: {
      width: "auto",
      marginRight: "1rem",
      marginBottom: "0",
    },
  }),
);

const DealForm: FunctionComponent<Props> = ({
  loading,
  guestUserCreated,
  onSubmit,
  redemptionButtons,
  companyNamePlaceholderText,
  emailAddressPlaceholderText,
  fullNamePlaceholderText,
  phoneNumberPlaceholderText,
  coloredVariant,
  form,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { configuration } = useConfiguration();
  const { settings } = useTheme();
  const showTermsAndConditions =
    !form?.acknowledgmentHtml &&
    (!configuration?.customDomain || settings?.termsAndConditions);

  const validationSchema = useMemo(() => {
    let schema = yup.object().shape({
      email: yup
        .string()
        .transform((value) => value.trim())
        .email(t("information_email_valid"))
        .required(t("information_email_required")),
      signUpOrganizationName: yup
        .string()
        .required(t("information_organization_name_required")),
      ...(form?.collectPhoneNumber
        ? {
            phoneNumber: yup
              .string()
              .required(t("information_phone_number_required"))
              .matches(
                /^(?=(?:.*\d){3,})[a-zA-Z0-9\s+().\-#/,]*$/,
                t("information_phone_number_invalid"),
              ),
          }
        : {}),
      fullName: yup.string().required(t("information_user_full_name_required")),
    });

    if (form?.acknowledgmentHtml) {
      schema = schema.shape({
        acknowledgmentTerms: yup
          .boolean()
          .oneOf([true], t("information_acknowledgment_required")),
      });
    }

    if (showTermsAndConditions) {
      schema = schema.shape({
        termsAndConditions: yup
          .boolean()
          .oneOf([true], t("information_terms_and_conditons")),
      });
    }

    return schema;
  }, [showTermsAndConditions, form, t]);

  const userEmail = user?.email;
  const userOrgName = user?.organization?.name || user?.signUpOrganizationName;
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const userPhoneNumber = user?.phoneNumber || "";
  const guestUser = isGuestUser(user);
  const { control, formState, handleSubmit } = useForm<GuestFlowRedemption>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: userEmail,
      signUpOrganizationName: userOrgName,
      fullName:
        userFirstName || userLastName
          ? `${userFirstName || ""} ${userLastName || ""}`.trim()
          : "",
      termsAndConditions: false,
      acknowledgmentTerms: false,
      ...(form?.collectPhoneNumber
        ? {
            phoneNumber: userPhoneNumber,
          }
        : {}),
    },
  });

  const [hadEmailChanged, setHadEmailChanged] = useState(false);

  function updateHadEmailChanged(e: React.ChangeEvent<HTMLInputElement>) {
    return setHadEmailChanged(checkIfEmailChanged(e.target.value, userEmail));
  }

  const { claimNow, getApproval, resendApproval } =
    redemptionButtons?.emailRedemptions || {};
  const claimText = claimNow;
  const approvalText =
    guestUserCreated && !hadEmailChanged ? resendApproval : getApproval;

  return (
    <StyledForm
      alignment={form?.alignment}
      onSubmit={handleSubmit(onSubmit)}
      aria-label={t("accessibility_registration_form")}
    >
      {form?.textBoxAbove && <HTMLRichText html={form.textBoxAbove} />}
      <StyledInputsContainer>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <StyledTextField
              {...field}
              label={
                emailAddressPlaceholderText || EMAIL_ADDRESS_PLACEHOLDER_TEXT
              }
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateHadEmailChanged(e)
              }
              error={!!formState.errors.email}
              helperText={
                formState.errors.email ? formState.errors.email.message : ""
              }
            />
          )}
        />
        <Controller
          name="signUpOrganizationName"
          control={control}
          render={({ field }) => (
            <StyledTextField
              {...field}
              label={
                companyNamePlaceholderText || COMPANY_NAME_PLACEHOLDER_TEXT
              }
              error={!!formState.errors.signUpOrganizationName}
              helperText={
                formState.errors.signUpOrganizationName
                  ? formState.errors.signUpOrganizationName.message
                  : ""
              }
            />
          )}
        />
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => (
            <StyledTextField
              {...field}
              label={fullNamePlaceholderText || FULL_NAME_PLACEHOLDER_TEXT}
              error={!!formState.errors.fullName}
              helperText={
                formState.errors.fullName
                  ? formState.errors.fullName.message
                  : ""
              }
            />
          )}
        />
        {form?.collectPhoneNumber && (
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                label={
                  phoneNumberPlaceholderText || PHONE_NUMBER_PLACEHOLDER_TEXT
                }
                error={!!formState.errors.phoneNumber}
                helperText={
                  formState.errors.phoneNumber
                    ? formState.errors.phoneNumber.message
                    : ""
                }
              />
            )}
          />
        )}
        <StyledButton
          startIcon={loading ? <ButtonProgress /> : null}
          variant="contained"
          type="submit"
          errors={!!Object.keys(formState.errors).length}
          disabled={loading}
        >
          {user?.id && !guestUser && !hadEmailChanged
            ? claimText
            : approvalText}
        </StyledButton>
      </StyledInputsContainer>
      {form?.textBoxBelow && <HTMLRichText html={form.textBoxBelow} />}
      {showTermsAndConditions && (
        <Controller
          name="termsAndConditions"
          control={control}
          render={({ field }) => (
            <CheckboxFieldContainer>
              <Terms
                control={
                  <StyledCheckbox
                    {...field}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    customColor={
                      coloredVariant ? form?.formTextColor : undefined
                    }
                  />
                }
                label={
                  <Typography>
                    <TermsText />
                  </Typography>
                }
              />
              {formState.errors.termsAndConditions && (
                <FormHelperText error>
                  {formState.errors.termsAndConditions.message}
                </FormHelperText>
              )}
            </CheckboxFieldContainer>
          )}
        />
      )}
      {form?.acknowledgmentHtml && (
        <Controller
          name="acknowledgmentTerms"
          control={control}
          render={({ field }) => (
            <CheckboxFieldContainer>
              <Terms
                control={
                  <StyledCheckbox
                    {...field}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    customColor={
                      coloredVariant ? form?.formTextColor : undefined
                    }
                  />
                }
                label={<HTMLRichText html={form.acknowledgmentHtml} />}
              />
              {formState.errors.acknowledgmentTerms && (
                <FormHelperText error>
                  {formState.errors.acknowledgmentTerms.message}
                </FormHelperText>
              )}
            </CheckboxFieldContainer>
          )}
        />
      )}
    </StyledForm>
  );
};

export default DealForm;
